import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  putMultipleFundingSourceForClientByClientIdRequest,
  resetPolicyExtractorState,
  selectClient,
  selectPolicyExtractor,
  selectSession,
  selectSessionAdvisor,
} from '@client/model';
import {
  checkPolicyCalculationTypeEquals,
  defaultsRetroactiveWaitingPeriodReimbursement,
  FundingPolicyType,
  fundingPolicyTypeDefs,
  isCareMattersFamily,
  isNullUndefinedOrEmpty,
  SessionType,
} from '@shared';
import { useSearchParams } from 'react-router-dom';
import { policyExtractedDataToPolicyState } from './policyExtractedDataToPolicyState';
import { getAssetCareDerivedPoolDollars } from './assetCareDerivedPools';

function policyFilesAreEqual(a: File | null, b: File | null): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
}

export const usePolicyForm = (fundingSourceId: string | undefined, onClose: () => void) => {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const {
    mutableClientPartner,
    intakeSurvey: { clientHasStartedLtc },
  } = client;
  const policyInitialState = useInitialPolicyState(fundingSourceId);
  const [policyState, setPolicyState] = useState<PolicyFundingSource>(policyInitialState);
  const [formHasBeenSubmitted, setFormHasBeenSubmitted] = useState(false);
  const [formErrors, setFormErrors] = useState<InsuranceFormErrors>({});
  const [hasChangedField, setHasChangedField] = useState(false);
  const [policyFile, setPolicyFile] = useState<File | null>(null);
  const { processedResponse, policyType } = useSelector(selectPolicyExtractor);
  const [showJointPolicyNoPartnerDialog, setShowJointPolicyNoPartnerDialog] = useState(false);
  const [maxBenefitsInterval, setMaxBenefitsInterval] = useState<MaxBenefitsIntervalType>(
    policyInitialState?.maxBenefitsInterval || 'daily',
  );
  const [searchParams, setSearchParams] = useSearchParams();

  // Ensure policyState and maxBenefitsInterval are updated when fundingSourceId changes
  useEffect(() => {
    setPolicyState(policyInitialState);
    setMaxBenefitsInterval(policyInitialState?.maxBenefitsInterval || 'daily');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fundingSourceId]);

  // Asset Care sizes its Acceleration (AOB) and Continuation (COB) pools as
  // monthly benefit × benefit period. The Face Amount IS the AOB pool (the
  // acceleration draws down the death benefit) and the COB dollar amount is the
  // COB pool, so both are derived from the monthly benefit and the duration
  // fields rather than entered independently. Keep policyState in sync with the
  // product so the saved record, the policy-details view, the report, and the
  // projection all match the read-only derived form fields. The same-reference
  // bail-out keeps this from looping once the stored values already match.
  useEffect(() => {
    const { face, cob } = getAssetCareDerivedPoolDollars(policyState);
    if (face == null && cob == null) return;
    setPolicyState(prevState => {
      let changed = false;
      const nextState = { ...prevState };
      if (face != null && prevState.policyMaximumBenefitAmount !== face) {
        nextState.policyMaximumBenefitAmount = face;
        changed = true;
      }
      if (cob != null && prevState.policyContinuationBenefitAmount !== cob) {
        nextState.policyContinuationBenefitAmount = cob;
        changed = true;
      }
      return changed ? nextState : prevState;
    });
  }, [policyState]);

  const handleChangePolicyField =
    (policyKey: PolicyFieldKey, parser?: (value: PolicyFieldValue) => PolicyFieldValue) =>
    (value: PolicyFieldValue) => {
      const parsedValue = parser ? parser(value) : value;
      setPolicyState(prevState => {
        const nextState = {
          ...prevState,
          [policyKey]: parsedValue,
        } as PolicyFundingSource;
        // When the policy type is switched to a product whose EP is paid back
        // retroactively, seed the toggle to true for users who haven't yet
        // expressed an explicit preference. Never clobber an existing value.
        if (
          policyKey === 'policyType' &&
          prevState.hasRetroactiveWaitingPeriodReimbursement == null &&
          defaultsRetroactiveWaitingPeriodReimbursement(parsedValue as FundingPolicyType | null)
        ) {
          nextState.hasRetroactiveWaitingPeriodReimbursement = true;
        }
        return nextState;
      });
      setHasChangedField(true);
    };

  const handleIntervalChange = (interval: MaxBenefitsIntervalType) => {
    setMaxBenefitsInterval(interval);
    setHasChangedField(true);
  };

  const resetState = () => {
    setPolicyState(policyInitialState);
    setFormHasBeenSubmitted(false);
    setFormErrors({});
    setHasChangedField(false);
    setPolicyFile(null);
    setMaxBenefitsInterval(policyInitialState?.maxBenefitsInterval || 'daily');
    dispatch(resetPolicyExtractorState());
  };

  const validateForm = (newPolicyState?: PolicyFundingSource) => {
    const policyStateToValidate = newPolicyState || policyState;

    let formIsValid = true;
    const newFormErrors = {} as InsuranceFormErrors;

    if (!policyStateToValidate.policyType) {
      newFormErrors.policyType = true;
      formIsValid = false;
    }

    const policyTypeDef = policyStateToValidate.policyType
      ? fundingPolicyTypeDefs[policyStateToValidate.policyType]
      : undefined;
    const isPolicyJoint = policyTypeDef?.isJoint;

    const isPolicyTypeAnnuityHybrid = checkPolicyCalculationTypeEquals(
      policyStateToValidate.policyType,
      FundingPolicyType.annuityHybrid,
    );
    const isPolicyTypeIndexedAnnuityCare =
      policyStateToValidate.policyType === FundingPolicyType.indexedAnnuityCare;
    const isPolicyTypeHybridIRA =
      !isPolicyTypeIndexedAnnuityCare &&
      checkPolicyCalculationTypeEquals(
        policyStateToValidate.policyType,
        FundingPolicyType.hybridLifeIra,
      );

    if (isPolicyJoint && !mutableClientPartner) {
      newFormErrors.jointPolicyNoPartner = true;
      formIsValid = false;
      setShowJointPolicyNoPartnerDialog(true);
    }

    if (isNullUndefinedOrEmpty(policyStateToValidate.policyPremiumStartYear)) {
      newFormErrors.policyPremiumStartYear = true;
      formIsValid = false;
    }

    // require policy monthly withdrawal rate for hybrid life IRA policies
    if (
      isPolicyTypeHybridIRA &&
      isNullUndefinedOrEmpty(policyStateToValidate.policyMonthlyWithdrawalRate)
    ) {
      newFormErrors.policyMonthlyWithdrawalRate = true;
      formIsValid = false;
    }

    if (
      isPolicyTypeHybridIRA &&
      isNullUndefinedOrEmpty(policyStateToValidate.policyMaximumBenefitAmount)
    ) {
      newFormErrors.policyMaximumBenefitAmount = true;
      formIsValid = false;
    }

    if (isPolicyTypeIndexedAnnuityCare) {
      if (
        isNullUndefinedOrEmpty(policyStateToValidate.policyBaseBenefitAmountAtIssue) &&
        isNullUndefinedOrEmpty(policyStateToValidate.policyMaximumBenefitAmount)
      ) {
        newFormErrors.policyBaseBenefitAmountAtIssue = true;
        formIsValid = false;
      }
      if (isNullUndefinedOrEmpty(policyStateToValidate.dailyHomeBenefitAmount)) {
        newFormErrors.dailyHomeBenefitAmount = true;
        formIsValid = false;
      }
      // When a COB rider balance is present, the COB benefit period is required:
      // the projection derives the COB monthly drawdown cap from balance ÷
      // period, so a missing period would otherwise silently fall back to an
      // assumed default. Policies without a COB rider leave the balance at 0/blank
      // and are not required to supply a period.
      const cobBalance =
        policyStateToValidate.policyContinuationBenefitAmountAtIssue ??
        policyStateToValidate.policyContinuationBenefitAmount ??
        0;
      if (
        cobBalance > 0 &&
        isNullUndefinedOrEmpty(policyStateToValidate.policyEobBenefitDurationMonths)
      ) {
        newFormErrors.policyEobBenefitDurationMonths = true;
        formIsValid = false;
      }
    }

    // on-claim specific checks
    if (clientHasStartedLtc) {
      if (isNullUndefinedOrEmpty(policyStateToValidate.policyBenefitUtilizedToDate)) {
        newFormErrors.policyBenefitUtilizedToDate = true;
        formIsValid = false;
      }
      if (
        policyStateToValidate.policyBenefitUtilizedToDate &&
        policyStateToValidate.policyBenefitUtilizedToDate > 0 &&
        policyStateToValidate.homeCareWaitingPeriodDays &&
        policyStateToValidate.homeCareWaitingPeriodDays > 0 &&
        policyStateToValidate.facilityCareWaitingPeriodDays &&
        policyStateToValidate.facilityCareWaitingPeriodDays > 0
      ) {
        newFormErrors.waitingPeriodAndBenefitsUtilized = true;
        formIsValid = false;
      }
    }

    // Require policyBenefitPeriodMonths for short-term care insurance and the
    // CareMatters family. CM needs it to size the base pool — without it, the
    // dedicated CM calc produces $0 LTC benefit. Quote-imported CM policies
    // populate this from sourceQuote.ltcBenefitDurationMonths automatically;
    // manual entries must supply it explicitly.
    if (
      checkPolicyCalculationTypeEquals(
        policyStateToValidate.policyType,
        FundingPolicyType.shortTermCareInsurance,
      ) ||
      isCareMattersFamily(policyStateToValidate.policyType)
    ) {
      if (isNullUndefinedOrEmpty(policyStateToValidate.policyBenefitPeriodMonths)) {
        newFormErrors.policyBenefitPeriodMonths = true;
        formIsValid = false;
      }
    }

    // require premium monthly cost for pre-claim users with non-annuity hybrid policies
    if (
      !clientHasStartedLtc && //pre-claim
      !isPolicyTypeAnnuityHybrid // not hybrid and pre-claim
    ) {
      if (isNullUndefinedOrEmpty(policyStateToValidate.policyPremiumMonthlyCost)) {
        newFormErrors.policyPremiumMonthlyCost = true;
        formIsValid = false;
      }
    }

    // require lump sum payment for annuity hybrid policies
    if (isPolicyTypeAnnuityHybrid) {
      if (isNullUndefinedOrEmpty(policyStateToValidate.policyLumpSumPayment)) {
        newFormErrors.policyLumpSumPayment = true;
        formIsValid = false;
      }
    }

    if (policyStateToValidate.isIndemnityPolicyPayment) {
      if (isNullUndefinedOrEmpty(policyStateToValidate.dailyAssistedLivingBenefitAmount)) {
        newFormErrors.dailyAssistedLivingBenefitAmount = true;
        formIsValid = false;
      }
      if (isNullUndefinedOrEmpty(policyStateToValidate.dailyHomeBenefitAmount)) {
        newFormErrors.dailyHomeBenefitAmount = true;
        formIsValid = false;
      }
      if (isNullUndefinedOrEmpty(policyStateToValidate.dailyIndependentLivingBenefitAmount)) {
        newFormErrors.dailyIndependentLivingBenefitAmount = true;
        formIsValid = false;
      }
      if (isNullUndefinedOrEmpty(policyStateToValidate.dailyNursingHomeBenefitAmount)) {
        newFormErrors.dailyNursingHomeBenefitAmount = true;
        formIsValid = false;
      }
    }

    setFormErrors(newFormErrors);
    return formIsValid;
  };

  const onSubmit = () => {
    setFormHasBeenSubmitted(true);
    if (!validateForm()) {
      return;
    }

    dispatch(
      putMultipleFundingSourceForClientByClientIdRequest({
        clientId: client.clientId,
        fundingSourceType: 'ltcPolicy',
        fundingSourceData: {
          ...policyState,
          maxBenefitsInterval,
          policyAddedAt: policyState?.policyAddedAt || Date.now(),
        },
        fundingSourceId: fundingSourceId ?? null,
      }),
    );

    const newParams = new URLSearchParams(searchParams);
    newParams.set('downloadReport', 'true');
    setSearchParams(newParams, { replace: true });

    onClose();
    resetState();
  };

  useEffect(() => {
    // revalidate form when form has been submitted or there are form errors
    if (formHasBeenSubmitted || Object.keys(formErrors).length > 0) {
      validateForm();
    }
  }, [policyState]);

  const onImportPolicyFieldValues = (skipSubmit?: boolean) => {
    if (!processedResponse) {
      return;
    }
    const newPolicyState = policyExtractedDataToPolicyState(processedResponse, policyState, {
      policyType,
    });
    setPolicyState(newPolicyState);
    setHasChangedField(true);

    if (validateForm(newPolicyState) && !skipSubmit) {
      dispatch(
        putMultipleFundingSourceForClientByClientIdRequest({
          clientId: client.clientId,
          fundingSourceType: 'ltcPolicy',
          fundingSourceData: {
            ...newPolicyState,
            policyAddedAt: Date.now(),
          },
          fundingSourceId: null,
        }),
      );
      onClose();
      resetState();
    }
  };

  const handleFileUpload = (file: File | null) => {
    if (policyFilesAreEqual(policyFile, file)) return;
    setPolicyFile(file);
    setHasChangedField(true);
  };

  return {
    policyState,
    formErrors,
    hasChangedField,
    onSubmit,
    resetState,
    handleChangePolicyField,
    onImportPolicyFieldValues,
    showJointPolicyNoPartnerDialog,
    setShowJointPolicyNoPartnerDialog,
    policyFile,
    setPolicyFile: handleFileUpload,
    maxBenefitsInterval,
    handleMaxBenefitsIntervalChange: handleIntervalChange,
  };
};

export interface InsuranceFormErrors extends ObjectMap<boolean | undefined> {
  policyInflationProtection?: boolean;
  jointPolicyNoPartner?: boolean;
}

const emptyPolicyState = (
  isAdvisorSession: boolean,
  advisorId: string | undefined,
): PolicyFundingSource =>
  ({
    policyType: null,
    hasPolicyFunding: true,
    addedByAdvisorId: isAdvisorSession ? advisorId : undefined,
  }) as PolicyFundingSource;

const useInitialPolicyState = (fundingSourceId?: string) => {
  const { sessionType } = useSelector(selectSession);
  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;
  const advisor = useSelector(selectSessionAdvisor);
  const { multipleFundingSources } = useSelector(selectClient);
  if (fundingSourceId && multipleFundingSources) {
    const existing = multipleFundingSources.ltcPolicy[fundingSourceId];
    if (existing) {
      return existing;
    }
  }
  return emptyPolicyState(isAdvisorSession, advisor?.advisorId);
};
